/**
 * Payment QR code payload builders.
 *
 * SPD/SPAYD — Czech & Slovak banks.
 * EPC QR (GiroCode) — SEPA-zone banks (AT, DE, CH, etc.).
 */
import { normalizeVariableSymbol } from "./variableSymbol";

export interface PaymentQRParams {
  iban?: string;
  bic?: string;
  amount: number;
  currency: string;
  variableSymbol?: string;
  /** Konstantní symbol (SPD X-KS): digits, at most 10. */
  constantSymbol?: string;
  /** Specifický symbol (SPD X-SS): digits, at most 10. */
  specificSymbol?: string;
  recipientName?: string;
  /**
   * The recipient's name in the SPD itself (RN, at most 35 characters). Kept apart
   * from `recipientName` (used by EPC), so the invoice QR codes stay as they are.
   */
  spdRecipientName?: string;
  message?: string;
  dueDate?: string; // ISO "YYYY-MM-DD"
}

/** SPD X-VS must be numeric and at most 10 digits — see ./variableSymbol. */
function sanitizeVS(vs: string): string {
  return normalizeVariableSymbol(vs) ?? "";
}

/** SPD MSG: keep alphanumeric, spaces, and basic punctuation. Uppercase for max compatibility. */
function sanitizeMSG(msg: string): string {
  return msg
    .replace(/[^\w\s,/-]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase()
    .substring(0, 60);
}

/**
 * SPD (Short Payment Descriptor) v1.0
 * Spec: https://qr-platba.cz/pro-vyvojare/specifikace-formatu/
 */
export function buildSPD(params: PaymentQRParams): string {
  const parts = ["SPD*1.0"];

  if (params.iban) {
    parts.push(`ACC:${params.iban.replace(/\s/g, "")}`);
  }
  parts.push(`AM:${params.amount.toFixed(2)}`);
  parts.push(`CC:${params.currency}`);

  if (params.variableSymbol) {
    const vs = sanitizeVS(params.variableSymbol);
    if (vs) parts.push(`X-VS:${vs}`);
  }
  for (const [key, value] of [
    ["X-KS", params.constantSymbol],
    ["X-SS", params.specificSymbol],
  ] as const) {
    const digits = (value ?? "").replace(/\s+/g, "");
    if (/^\d{1,10}$/.test(digits)) parts.push(`${key}:${digits}`);
  }
  if (params.message) {
    const msg = sanitizeMSG(params.message);
    if (msg) parts.push(`MSG:${msg}`);
  }
  if (params.dueDate) {
    parts.push(`DT:${params.dueDate.replace(/-/g, "")}`);
  }
  // Only for SPD callers that ask for it (the invoice QR leaves the name out).
  if (params.spdRecipientName) {
    const rn = sanitizeMSG(params.spdRecipientName).substring(0, 35);
    if (rn) parts.push(`RN:${rn}`);
  }

  return parts.join("*");
}

/**
 * EPC QR / GiroCode v002
 * Spec: European Payments Council EPC069-12
 * Lines: BCD / 002 / 1 / SCT / BIC / Name / IBAN / EUR{amount} / (purpose) / (ref) / (text) / (info)
 */
export function buildEPC(params: PaymentQRParams): string {
  const lines = [
    "BCD", // Service tag
    "002", // Version
    "1", // Encoding (UTF-8)
    "SCT", // Identification
    params.bic || "", // BIC (optional in v002)
    (params.recipientName || "").substring(0, 70), // Beneficiary name
    (params.iban || "").replace(/\s/g, ""), // IBAN
    `${params.currency}${params.amount.toFixed(2)}`, // Amount
    "", // Purpose (empty)
    params.variableSymbol ? (normalizeVariableSymbol(params.variableSymbol) ?? "") : "", // Structured reference
    params.message ? params.message.substring(0, 140) : "", // Unstructured text
    "", // Beneficiary-to-originator info
  ];

  return lines.join("\n");
}

/**
 * Pick the right QR format based on the IBAN country prefix.
 * Returns the payload string, or null if no QR can be generated.
 */
export function buildPaymentQRPayload(params: PaymentQRParams): string | null {
  if (!params.iban || !params.amount || params.amount <= 0) return null;

  const prefix = params.iban.replace(/\s/g, "").substring(0, 2).toUpperCase();
  const cur = params.currency.toUpperCase();

  if ((prefix === "CZ" || prefix === "SK") && cur === "CZK") {
    return buildSPD(params);
  }

  return null;
}
